// Macro-History pure functions. 1:1 portiert aus V1 useMacroHistory.js.
// - buildMacroSnapshot: bauen aus FullData + MarketStats
// - trend: % Δ über N Tage
// - formatMacroTrendContext: lesbarer Text-Block für EVE-Prompts

import type { FullData, MarketStats } from '@/types/market';
import type { MacroSnapshotEntry, MacroTrend } from '@/types/macro-history';

/** V1-Konstante: max 30 Tage History (rolling-window). */
export const MAX_MACRO_DAYS = 30;

function numOrNull(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Baut den Tages-Snapshot aus FullData + MarketStats. V1-Konvention:
 * `change` wird via `parseFloat || null` koerziert (string `"5.20"` → 5.2,
 * leerer/missing String → null).
 */
export function buildMacroSnapshot(
  fullData: FullData,
  marketStats: MarketStats,
  now: Date = new Date(),
): MacroSnapshotEntry {
  const j = fullData.japan;
  const m = fullData.macro;
  const fngHistory =
    fullData.fng?.data?.slice(0, 7).map((d) => ({
      value: typeof d.value === 'number' ? d.value : parseInt(String(d.value), 10),
      label: d.value_classification ?? '',
    })) ?? [];

  return {
    date: now.toISOString().slice(0, 10),
    btc: marketStats.btc?.price ?? null,
    btcChange: numOrNull(marketStats.btc?.change),
    eth: marketStats.eth?.price ?? null,
    ethChange: numOrNull(marketStats.eth?.change),
    sol: marketStats.sol?.price ?? null,
    solChange: numOrNull(marketStats.sol?.change),
    btcDominance: fullData.global?.data?.market_cap_percentage?.btc ?? null,
    totalMcap: fullData.global?.data?.total_market_cap?.usd ?? null,
    btcFunding: numOrNull(fullData.funding.fundingRate),
    btcOI: numOrNull(fullData.oi.openInterest),
    ethFunding: numOrNull(fullData.ethFunding.fundingRate),
    dxy: m?.dxy?.price ?? null,
    dxyChange: numOrNull(m?.dxy?.change),
    vix: m?.vix?.price ?? null,
    vixChange: numOrNull(m?.vix?.change),
    spx: m?.spx?.price ?? null,
    spxChange: numOrNull(m?.spx?.change),
    gold: m?.gold?.price ?? null,
    goldChange: numOrNull(m?.gold?.change),
    oil: m?.oil?.price ?? null,
    oilChange: numOrNull(m?.oil?.change),
    tlt: m?.tlt?.price ?? null,
    silver: m?.silver?.price ?? null,
    us10y: m?.us10y?.price ?? null,
    jp10y: j?.jp10y?.value ?? null,
    usdjpy: j?.usdjpy?.value ?? null,
    carrySpread: j?.bondSpread ?? null,
    fng: marketStats.fng?.value ?? null,
    fngLabel: marketStats.fng?.label ?? null,
    fngHistory,
    regime: marketStats.regime ?? null,
  };
}

/**
 * Berechnet % Δ eines Feldes über die letzten `days`. V1-Verhalten:
 * - <2 valide Datenpunkte → null
 * - first === 0 oder NaN → null
 * - Rest: `((last-first)/|first|)*100` auf 2 Nachkommastellen
 */
export function trend(
  history: MacroSnapshotEntry[],
  key: keyof MacroSnapshotEntry,
  days = 7,
): MacroTrend | null {
  const relevant = history
    .slice(-days)
    .filter((s) => s[key] !== null && s[key] !== undefined);
  if (relevant.length < 2) return null;
  const first = Number(relevant[0]![key]);
  const last = Number(relevant[relevant.length - 1]![key]);
  if (!Number.isFinite(first) || !Number.isFinite(last) || first === 0) return null;
  const delta = parseFloat((((last - first) / Math.abs(first)) * 100).toFixed(2));
  return { from: first, to: last, delta, days: relevant.length };
}

function fmtTrend(t: MacroTrend | null, unit = ''): string {
  if (!t) return 'keine Daten';
  const dir = t.delta > 0 ? '↑' : t.delta < 0 ? '↓' : '→';
  const sign = t.delta > 0 ? '+' : '';
  return `${t.from}${unit} → ${t.to}${unit} (${sign}${t.delta}% in ${t.days}d) ${dir}`;
}

function carryWarning(t: MacroTrend | null): string {
  if (!t) return '';
  if (t.delta < -0.3) return '⚠️ Spread verengt sich — Carry-Trade-Positionen werden abgebaut';
  if (t.delta > 0.3) return '📈 Spread weitet sich — Carry-Trade-Risiko steigt';
  return '→ Stabil';
}

/**
 * V1 `getMacroTrendContext` — lesbarer Block für EVE-Prompts.
 * - <2 History-Einträge → null
 * - Zeigt 7d-Trends (`t7`) + 14d-Trend für `jp10y` (monatlich)
 * - Letzter Snapshot wird als „heute"-Referenz genutzt
 */
export function formatMacroTrendContext(history: MacroSnapshotEntry[]): string | null {
  if (history.length < 2) return null;

  const t7 = (key: keyof MacroSnapshotEntry): MacroTrend | null => trend(history, key, 7);
  const t14 = (key: keyof MacroSnapshotEntry): MacroTrend | null => trend(history, key, 14);
  const last = history[history.length - 1]!;

  const formatChange = (v: number | null): string =>
    v != null ? `${v > 0 ? '+' : ''}${v}%` : '—';

  const lines: Array<string | null> = [
    `MAKRO-TRENDS (${history.length} Tage History, letzter Snapshot: ${last.date}):`,
    '',
    `── CRYPTO ──`,
    `BTC:          ${fmtTrend(t7('btc'), '$')} | heute 24h: ${formatChange(last.btcChange)}`,
    `ETH:          ${fmtTrend(t7('eth'), '$')} | heute 24h: ${formatChange(last.ethChange)}`,
    `SOL:          ${fmtTrend(t7('sol'), '$')} | heute 24h: ${formatChange(last.solChange)}`,
    last.btcDominance != null ? `BTC Dominanz: ${last.btcDominance.toFixed(1)}%` : null,
    last.btcFunding != null
      ? `BTC Funding:  ${(last.btcFunding * 100).toFixed(4)}% (positiv = Longs dominieren)`
      : null,
    '',
    `── MAKRO ──`,
    `DXY:          ${fmtTrend(t7('dxy'))}`,
    `VIX:          ${fmtTrend(t7('vix'))}`,
    `SPX:          ${fmtTrend(t7('spx'), '$')}`,
    `Gold:         ${fmtTrend(t7('gold'), '$')}`,
    `Oil (WTI):    ${fmtTrend(t7('oil'), '$')}`,
    last.tlt != null ? `TLT:          ${fmtTrend(t7('tlt'), '$')}` : null,
    '',
    `── ZINSEN / JAPAN ──`,
    `US 10Y:       ${fmtTrend(t7('us10y'), '%')}`,
    `JP 10Y:       ${fmtTrend(t14('jp10y'), '%')} (monatlich)`,
    `USD/JPY:      ${fmtTrend(t7('usdjpy'))}`,
    `CarrySpread:  ${fmtTrend(t7('carrySpread'), '%')} — ${carryWarning(t7('carrySpread'))}`,
    '',
    `── SENTIMENT ──`,
    `Fear&Greed:   ${fmtTrend(t7('fng'))} (heute: ${last.fngLabel ?? '—'})`,
    last.fngHistory.length > 0
      ? `F&G 7d:       ${last.fngHistory.map((d) => `${d.value}(${(d.label ?? '').split(' ')[0]})`).join(' → ')}`
      : null,
    last.regime ? `Regime:       ${last.regime}` : null,
  ];

  return lines.filter((l): l is string => l !== null).join('\n');
}
